/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { GRID_SORT_DIRECTIONS } from '@veeam/components';

import { GridSortDirections, SortingMode } from '../../../enums';
import { GridColumnProps, SortRequestParams } from '../../../interfaces';
import { RequestSuccessResponse } from '../../../../../interfaces';
import { createSortFnByField } from '../../../../../helpers/string-helpers';
import { SortProps } from '../../../interfaces/sort-props';

import type { RequestGridSortDirections } from '../../../enums';
import type { GridProps, GridStorageActions } from '../../../interfaces';
import type { GridStore } from '../grid-store';

export class SortingStore<RowDataT, TotalRowDataT, FilterT> {
    @observable sortingConfig: SortProps;
    @observable sortingMode: SortingMode;

    constructor(
        private gridStore: GridStore<RowDataT, TotalRowDataT, FilterT>,
        props: GridProps<RowDataT, TotalRowDataT, FilterT>,
        private gridStorageActions: GridStorageActions<FilterT> | null
    ) {
        makeObservable(this);

        const sortingFromLocalStorage = gridStorageActions?.getSortData();
        const forcedSort = props.initialState?.isForce && props.initialState.sort;

        this.sortingConfig = forcedSort || sortingFromLocalStorage || props.initialState?.sort; // priority: forced initial sort => sort from local storage => initial sort
        this.sortingMode = props.sortingMode ?? SortingMode.Remote;
    }

    @action.bound
    applySorting(columnId: string): void {
        let sorting;

        if (this.sortingConfig?.direction) {
            const newSortDirection = this.sortingConfig.direction === GridSortDirections.Asc
                ? GRID_SORT_DIRECTIONS.desc
                : GRID_SORT_DIRECTIONS.asc;

            sorting = { direction: newSortDirection, key: columnId };
        } else {
            sorting = { direction: GRID_SORT_DIRECTIONS.asc, key: columnId };
        }

        this.gridStorageActions?.onSortChange(sorting);
        this.sortingConfig = sorting;

        if (this.sortingMode === SortingMode.Local) {
            const items = toJS(this.gridStore.paginationStore.data);

            this.gridStore.paginationStore.data = this.doLocalSorting(items);
        }

        if (this.sortingMode === SortingMode.Remote) {
            this.gridStore.goToPage(1);
        }
    }

    @computed
    get sortRequestParams(): SortRequestParams {
        if (this.sortingConfig && this.sortingMode === SortingMode.Remote) {
            return [{
                property: this.sortingConfig.key,
                direction: this.sortingConfig.direction.toUpperCase() as RequestGridSortDirections,
            }];
        }
    }

    @action.bound
    changeSortingMode(sortingMode: SortingMode): void {
        this.sortingMode = sortingMode;

        if (this.sortingMode === SortingMode.Disabled) {
            this.sortingConfig = undefined;
        }

        this.gridStore.columnsStore.setColumns(this.gridStore.columnsStore.columns);
    }

    modifySortBeforeRequest(sortParams: SortRequestParams): SortRequestParams {
        if (!sortParams) return;

        let sortParam = sortParams[0];
        const sortedColumn = this.gridStore.columnsStore.columns.find(col => col.id === sortParam.property);

        if (sortedColumn?.sortKey) {
            sortParam = {
                direction: sortParam.direction,
                property: sortedColumn.sortKey,
            };
        }

        if (sortedColumn?.getAdditionalSorters) {
            const additionalSorters = sortedColumn.getAdditionalSorters(sortParam);

            return [sortParam].concat(additionalSorters);
        }

        return [sortParam];
    }

    @action.bound
    doLocalSortingIfRequired(response: RequestSuccessResponse<RowDataT[]>): RequestSuccessResponse<RowDataT[]> {
        if (this.sortingConfig && this.sortingMode === SortingMode.Local) {
            response.data = this.doLocalSorting(response.data);
        }

        return response;
    }

    @action.bound
    doLocalSorting(items: RowDataT[] = []): RowDataT[] {
        const { key, direction } = this.sortingConfig;
        const currentSortColumn = this.gridStore.columnsStore.columns.find(({ id }) => id === key) || null;

        items.sort(currentSortColumn?.sorterFn || createSortFnByField<RowDataT>(key as keyof RowDataT));

        if (direction === GridSortDirections.Desc) {
            items.reverse();
        }

        return items;
    }

    @action.bound
    onHeaderClick(column: GridColumnProps<RowDataT>): void {
        if (this.sortingMode === SortingMode.Disabled || column.isSortable === false || this.gridStore.requestStore.loading) {
            return;
        }

        this.applySorting(column.id);
    }
}
